import React, { useState } from 'react';
import { Button } from '../../ui/button';
import { toast } from 'react-hot-toast';
import { Plus, Loader2 } from 'lucide-react';
import { EMPTY_SALES_DATA } from './sales-view/salesDataDefaults';
import { useSalesMasterData } from './sales-view/useSalesMasterData';
import SalesDataCard from './sales-view/SalesDataCard';
import SalesDataDialog from './sales-view/SalesDataDialog';

const SalesView = ({ formData, setFormData }) => {
  const masterData = useSalesMasterData();
  const { loading, getSalesOrgName, getDistChannelName, getPlantName } = masterData;

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [currentSalesData, setCurrentSalesData] = useState({ ...EMPTY_SALES_DATA });

  const handleOpenDialog = (index = null) => {
    if (index !== null) {
      setEditingIndex(index);
      setCurrentSalesData(formData.sales_data?.[index] || { ...EMPTY_SALES_DATA });
    } else {
      setEditingIndex(null);
      setCurrentSalesData({ ...EMPTY_SALES_DATA });
    }
    setIsDialogOpen(true);
  };

  const handleSaveSalesData = () => {
    const required = [
      ['sales_org_id', 'Sales Organization'],
      ['dist_channel_id', 'Distribution Channel'],
      ['plant_id', 'Plant'],
      ['sales_unit', 'Sales Unit'],
      ['availability_check', 'Availability Check'],
      ['profit_center', 'Profit Center'],
      ['transportation_group', 'Transportation Group'],
      ['loading_group', 'Loading Group'],
    ];
    for (const [field, label] of required) {
      if (!currentSalesData[field]) {
        toast.error(`Please select ${label}`);
        return;
      }
    }

    // Duplicate Sales Org + Distribution Channel combination (Plant can differ).
    const salesDataArray = formData.sales_data || [];
    const isDuplicate = salesDataArray.some((item, idx) => {
      if (editingIndex !== null && idx === editingIndex) return false;
      return (
        item.sales_org_id === currentSalesData.sales_org_id &&
        item.dist_channel_id === currentSalesData.dist_channel_id
      );
    });
    if (isDuplicate) {
      toast.error(
        'This Sales Organization + Distribution Channel combination already exists. You cannot add the same Sales Org and Distribution Channel twice.'
      );
      return;
    }

    setFormData((prev) => {
      const updated = [...(prev.sales_data || [])];
      if (editingIndex !== null) {
        updated[editingIndex] = currentSalesData;
        toast.success('Sales data updated successfully');
      } else {
        updated.push(currentSalesData);
        toast.success('Sales data added successfully');
      }
      return { ...prev, sales_data: updated };
    });
    setIsDialogOpen(false);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-10 h-10 animate-spin text-orange-600 mb-3" />
        <p className="text-sm text-gray-600">Loading master data...</p>
      </div>
    );
  }

  const salesDataArray = formData.sales_data || [];
  const names = { getSalesOrgName, getDistChannelName, getPlantName };

  return (
    <div className="space-y-6" data-testid="material-sales-view">
      <div className="flex items-center justify-between border-b border-gray-200 pb-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Sales Data</h3>
          <p className="text-sm text-gray-600 mt-1">
            Add sales data for different Sales Organization, Distribution Channel, and Plant combinations
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="flex items-center gap-2" data-testid="add-sales-data-btn">
          <Plus className="w-4 h-4" />
          Add Sales Data
        </Button>
      </div>

      {salesDataArray.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-gray-300 rounded-lg">
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Plus className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-base font-medium text-gray-900 mb-1">No sales data added yet</h3>
            <p className="text-sm text-gray-600 mb-4">
              Click "Add Sales Data" to create sales data for specific sales organizations
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {salesDataArray.map((salesData, index) => (
            <SalesDataCard
              key={index}
              salesData={salesData}
              index={index}
              names={names}
              onEdit={handleOpenDialog}
            />
          ))}
        </div>
      )}

      <SalesDataDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        editingIndex={editingIndex}
        currentSalesData={currentSalesData}
        setCurrentSalesData={setCurrentSalesData}
        masterData={masterData}
        onSave={handleSaveSalesData}
      />
    </div>
  );
};

export default SalesView;
